"use client";

import { useCallback, useEffect, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  SkeletonContainer,
  SkeletonInput,
  SkeletonLabel,
} from "@/components/ui/skeleton";
import { Label } from "./label";

interface ArchiveConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  entityType: string;
  entityName: string;
  /** Project/entity title still loading (e.g. from URL + fetch). */
  detailsLoading?: boolean;
  loading?: boolean;
  description?: string;
  confirmationLabel?: string;
}

export function ArchiveConfirmModal({
  open,
  onClose,
  onConfirm,
  entityType,
  entityName,
  detailsLoading = false,
  loading = false,
  description,
  confirmationLabel,
}: ArchiveConfirmModalProps) {
  const [confirmation, setConfirmation] = useState("");
  const [displayEntityName, setDisplayEntityName] = useState(entityName);

  useEffect(() => {
    if (!open) return;
    if (entityName) setDisplayEntityName(entityName);
    setConfirmation("");
  }, [open, entityName]);

  const quotedEntityName = `"${displayEntityName}"`;

  const handleClose = useCallback(() => {
    if (loading) return;
    setConfirmation("");
    onClose();
  }, [loading, onClose]);

  const isMatch = confirmation.trim() === displayEntityName.trim();

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => { if (!nextOpen) handleClose(); }}
      dismissible={!loading}
    >
      <ModalContent size="md">
        <ModalHeader
          title={`Archive ${entityType}`}
          iconName="IconTrashCanSimple"
        />

        <ModalBody>
        {detailsLoading ? (
          <SkeletonContainer
            aria-label={`Loading ${entityType} details`}
            className="flex-col gap-4"
          >
            <SkeletonLabel className="h-3.5 w-3/4" />
            <div className="flex flex-col gap-1.5">
              <SkeletonLabel className="h-3.5 w-full" />
              <SkeletonLabel className="h-3.5 w-5/6" />
            </div>
            <div className="flex flex-col gap-1.5">
              <SkeletonLabel className="h-3.5 w-40" />
              <SkeletonInput className="h-9 w-full rounded-md" />
              <SkeletonLabel className="h-3 w-2/3" tone="muted" />
            </div>
          </SkeletonContainer>
        ) : (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-contrast/60 leading-normal">
            {`You're about to archive ${quotedEntityName}.`}
          </p>

          <p className="text-sm text-contrast/60 leading-normal">
            {description ||
              `This will remove the ${entityType} from active views and keep it out of your main workflow.`}
          </p>

          <div className="flex flex-col gap-1">
            <Label>
              {confirmationLabel || `Type ${quotedEntityName} to confirm`}
            </Label>
            <Input
              type="text"
              variant="modal"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              placeholder={displayEntityName}
              className="w-full p-2 text-sm text-heading placeholder:text-contrast/40 focus:border-contrast/30"
              onKeyDown={(event) => {
                if (event.key === "Enter" && isMatch && !loading) {
                  onConfirm();
                }
              }}
              disabled={loading}
            />
            <p className="text-xs text-contrast/40 leading-4">
              Enter the name exactly as shown, including spacing and punctuation.
            </p>
          </div>
        </div>
        )}
        </ModalBody>

        <ModalFooter>
          <Button type="button" variant="secondary" size="sm" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <SubmitButton type="button" variant="destructive" size="sm"
            onClick={onConfirm}
            disabled={!isMatch || detailsLoading}
            loading={loading}
              loadingLabel="Archiving"
          >
            {`Archive ${entityType}`}
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
